import {
  closeAccountHttp,
  convertManagedtoAdminHttp,
  reopenAccountHttp,
  sendUnreconciledEmail,
} from "../actions/accounts";
import config from "../config";
import browserHistory from "../helpers/browserHistory";
import { getActions, getTypes } from "../helpers/crudActions";
import HttpClient from "../helpers/httpclient";
import { normalizeMonitorViewServiceTeams } from "../helpers/monitorServiceTeamSelections";
import { createSubmissionId } from "../helpers/submissionId";

export function monitorSubmissionFailureMessage(status, submissionId) {
  if (status === 409) {
    return `The trade request was not saved because submission ID ${submissionId} was already used for different request details. Reload the page before submitting a corrected request.`;
  }
  const confirmedRejection = Number.isInteger(status) && status < 500 && status !== 408;
  if (confirmedRejection) {
    return `The trade request was not saved (HTTP ${status}). Review your access and request details; it is safe to submit again after correcting them. Reference submission ID: ${submissionId}.`;
  }
  return `We could not confirm the trade request. Do not submit it again; use this submission ID to check its status safely: ${submissionId}.`;
}

export default {
  ...getActions("MONITORS", "monitors"),
  ...getTypes("MONITORS"),
  UPDATE_MONITOR_REBALANCING_GROUP_PENDING: "UPDATE_MONITOR_REBALANCING_GROUP_PENDING",
  UPDATE_MONITOR_REBALANCING_GROUP_FULFILLED: "UPDATE_MONITOR_REBALANCING_GROUP_FULFILLED",
  save(object, redirect = true, submissionId) {
    submissionId ||= createSubmissionId();
    const headers = { "x-advisorspace-submission-id": submissionId };
    const terminal = new Set(["complete", "manual_review"]);
    const wait = () => new Promise((resolve) => setTimeout(resolve, 1500));
    const reconcile = async (response, attempts = 0) => {
      if (response.data?.id && response.data?.submission_status == null) return response;
      if (terminal.has(response.data?.submission_status)) return response;
      if (attempts >= 80) throw new Error("The saved trade request is still processing.");
      await wait();
      return reconcile(await submit(), attempts + 1);
    };
    const submit = () => HttpClient.post(`${config.api}/monitors/`, object, { headers });
    const request = submit()
      .then((response) => reconcile(response))
      .catch(async (error) => {
        if (error?.response?.status && error.response.status < 500 && error.response.status !== 408) throw error;
        await wait();
        return reconcile(await submit());
      })
      .then(this.shouldRedirect(redirect));
    return { type: "MONITORS_OBJECT_SAVE", payload: request };
  },
  redirect(response) {
    browserHistory.push(`/trade-request/${response.data.id}?new=true`);
    return response;
  },
};

export function fetchRebalancingGroup(number) {
  const url = `${config.api}/monitors/${number}`;
  return HttpClient.get(url).then((r) => r.data);
}

export function fetchRebalancingGroupOptions(household) {
  const url = `${config.api}/monitors/options`;
  const getOption = (r) => ({ value: r.number, label: `${r.name} (${r.number})`, object: r });
  return (input) => {
    const params = { params: { household, from: "select" } };
    if (input) {
      params.params.q = input;
    }
    return HttpClient.get(url, params).then((r) => ({ options: r.data.map(getOption) }));
  };
}

export function fetchRebalancingAccounts(id) {
  const url = `${config.api}/monitors/${id}/accounts`;
  return HttpClient.get(url).then((r) => r.data);
}

export function fetchMultipleRebalancingAccounts(ids) {
  const url = `${config.api}/monitors/accounts`;
  const params = { params: { ids } };
  return HttpClient.get(url, params).then((r) => r.data);
}

export function fetchCashReserves(id, accounts) {
  const url = `${config.api}/monitors/${id}/cash-reserves`;
  const params = { params: { accounts } };
  return HttpClient.get(url, params).then((r) => r.data);
}

export function fetchAlternateSecurity(id) {
  const url = `${config.api}/monitors/${id}/alternate-security`;
  return HttpClient.get(url).then((r) => r.data);
}

export function updateAlternateSecurity(id, alternateSecurity) {
  const url = `${config.api}/monitors/${id}/alternate-security`;
  const body = { alternate_security_set: alternateSecurity };
  return HttpClient.put(url, body);
}

export function fetchMonitorView(id = "default") {
  const url = `${config.api}/monitors/${id}/monitor-view`;
  return HttpClient.get(url).then((r) => r.data);
}

export function fetchMonitorValues() {
  return HttpClient.get(`${config.api}/monitors/monitor-values`).then((r) => r.data);
}

export function fetchMonitorViewOptions(_input) {
  const url = `${config.api}/monitors/monitor-view-options`;
  return HttpClient.get(url).then((r) => r.data);
}

export function saveMonitorView(view) {
  const url = `${config.api}/monitors${view.id ? `/${view.id}` : ""}/monitor-view`;
  const method = view.id ? "put" : "post";
  const normalizedView = normalizeMonitorViewServiceTeams(view);
  if (normalizedView.service_team_all === false && normalizedView.service_team_specific === "[]") {
    return Promise.reject(new Error("A restricted monitor view requires at least one valid service team."));
  }
  return HttpClient[method](url, normalizedView).then((r) => r.data);
}

export function deleteMonitorView(view) {
  return HttpClient.delete(`${config.api}/monitors/${view.id}/monitor-view`, view).then((r) => r.data);
}

export function fetchTargetSecurities(id) {
  const url = `${config.api}/monitors/${id}/target-securities`;
  return HttpClient.get(url).then((r) => r.data);
}

export function fetchSecurityRestrictionPrefill(id, symbol) {
  const url = `${config.api}/monitors/${id}/security-restriction-prefill/${symbol}`;
  return HttpClient.get(url).then((r) => r.data);
}

export function fetchDriftSummary(id, model, body) {
  const url = `${config.api}/monitors/${id}/summary/${model}`;
  const params = { params: { ...body } };
  return HttpClient.get(url, params).then((r) => r.data);
}

export function updateRebalancingGroup(accountActions) {
  const functions = {
    emailOperations: sendUnreconciledEmail,
    unmanage: convertManagedtoAdminHttp,
    close: closeAccountHttp,
    reopen: reopenAccountHttp,
  };
  const actions = accountActions.map((action) => functions[action.func](action.id));
  return {
    type: "UPDATE_MONITOR_REBALANCING_GROUP",
    payload: Promise.allSettled(actions),
  };
}
